/**
 * Helper function to check if an IP is IPv4 or IPv6.
 * @param {string} ip
 * @returns {boolean} True if IPv4, false otherwise.
 */
function isIPv4(ip) {
  // A basic check for the presence of dots typically signifies IPv4
  return typeof ip === 'string' && ip.includes('.')
}
/**
 * Helper function to expand shorthand IPv6 addresses to their full form.
 * Needed for accurate comparison.
 * @param {string} ip IPv6 address string
 * @returns {string} The fully expanded IPv6 address string.
 */
const expandIPv6 = (ip) => {
  if (!ip || !ip.includes('::')) return ip

  const parts = ip?.split('::')
  const head = parts[0]
  const tail = parts[1]
  const headParts = head.split(':').filter((p) => p.length > 0)
  const tailParts = tail ? tail.split(':').filter((p) => p.length > 0) : []

  const requiredSegments = 8
  const currentSegments = headParts.length + tailParts.length
  const zerosToAdd = requiredSegments - currentSegments

  if (zerosToAdd <= 0) {
    return ip // Already expanded or invalid format
  }

  const zeroSegment = Array(zerosToAdd)
    .fill('0')
    .join(':')

  // Reconstruct the full IP
  let expanded = ''
  if (headParts.length > 0) {
    expanded += headParts.join(':')
    if (tailParts.length > 0) {
      expanded += ':' + zeroSegment + ':' + tailParts.join(':')
    } else {
      expanded += ':' + zeroSegment
    }
  } else if (tailParts.length > 0) {
    expanded += zeroSegment + ':' + tailParts.join(':')
  } else {
    expanded = Array(requiredSegments)
      .fill('0')
      .join(':')
  }

  // Clean up any potential double colons created by reconstruction logic
  return expanded.replace(/::/g, ':')
}

// Funtion to sort both IPv4 and IPv6
export const sortIpAddress = (a = [], b = [], orderDirection = 'asc') => {
  // If a or b is an array, sort and compare their first IPs
  const getFirstSortedIp = (ip) => {
    if (Array.isArray(ip)) {
      const sorted = [...ip].sort(sortIpAddress)
      return orderDirection === 'desc' ? sorted[0] : sorted[sorted.length - 1]
    }
    return ip
  }
  const ipA = getFirstSortedIp(a)
  const ipB = getFirstSortedIp(b)

  const isA_IPv4 = isIPv4(ipA)
  const isB_IPv4 = isIPv4(ipB)

  // Rule 1: IPv4 comes before IPv6
  if (isA_IPv4 && !isB_IPv4) return 1
  if (!isA_IPv4 && isB_IPv4) return -1

  // Rule 2: Sort within the same family
  if (isA_IPv4) {
    // IPv4 comparison logic
    const makeIpNumber = (ip) =>
      Number(
        (ip || '')
          ?.split('.')
          ?.map((octet) => octet.padStart(3, '0'))
          ?.join(''),
      )

    // Returning b-a as when displayed reverse list is displayed
    return makeIpNumber(ipB) - makeIpNumber(ipA)
  } else {
    // IPv6 comparison logic
    const expandedA = expandIPv6(ipA || '')?.split(':')
    const expandedB = expandIPv6(ipB || '')?.split(':')

    for (let i = 0; i < 8; i++) {
      const valA = parseInt(expandedA[i], 16)
      const valB = parseInt(expandedB[i], 16)
      if (valA !== valB) {
        // Returning b-a as when displayed reverse list is displayed
        return valB - valA
      }
    }
    return 0 // IPs are identical
  }
}
